import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useTheme } from '../App';
import { 
  LayoutDashboard, 
  BarChart3, 
  Sparkles, 
  History, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  User,
  Shield,
  Brain,
  CreditCard,
  Users,
  Zap,
  Gift,
  Sun,
  Moon,
  Crown,
  Target,
  Layers,
  Trophy,
  Coins,
  ChevronDown
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import NotificationBell from './NotificationBell';

const Navbar = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/tauro', label: 'Tauro', icon: Brain, highlight: true },
    { path: '/consenso', label: 'Consenso', icon: Sparkles, highlight: true },
    { path: '/prometeo', label: 'Prometeo', icon: Zap, highlight: true },
    { path: '/jugada-del-dia', label: 'Jugada', icon: Trophy, highlight: true },
    { path: '/7ia', label: '7IA', icon: Zap, highlight: true },
    { path: '/power-number', label: 'Poderoso', icon: Crown, highlight: true },
    { path: '/aries', label: 'Aries', icon: Sparkles, highlight: true },
    { path: '/super-ia', label: 'Sócrates', icon: Brain, highlight: true },
    { path: '/precision', label: 'Precision', icon: Target },
    { path: '/pale-tripleta', label: 'Pale/Trip', icon: Layers },
    { path: '/affiliates', label: 'Afiliados', icon: Users },
    { path: '/promotions', label: 'Cupones', icon: Gift },
    { path: '/pricing', label: 'Planes', icon: Settings },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const primaryItems = navItems.slice(0, 6);
  const moreItems = navItems.slice(6);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-2">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
            <img src="/logo.png" alt="NumeroMaestro" className="h-10 w-auto" />
            <span className="font-bold text-lg text-white hidden xl:block">NumeroMaestro</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-0.5 min-w-0">
            {primaryItems.map((item, idx) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={`${idx >= 3 ? 'hidden lg:flex' : 'flex'} items-center gap-1.5 px-3 py-2 rounded-lg whitespace-nowrap transition-all duration-200 ${
                    isActive 
                      ? item.highlight 
                        ? 'bg-gradient-to-r from-purple-600/30 to-pink-600/30 text-purple-300 border border-purple-500/30' 
                        : 'bg-green-600/20 text-green-400'
                      : item.highlight
                        ? 'text-purple-400 hover:text-purple-300 hover:bg-purple-900/30 border border-transparent hover:border-purple-500/30'
                        : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  <Icon size={17} />
                  <span className="text-sm font-medium">{item.label}</span>
                </Link>
              );
            })}

            {/* Menu "Más" con el resto de herramientas */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-1 px-3 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 text-sm font-medium whitespace-nowrap transition-colors"
                  data-testid="nav-more-btn"
                >
                  Más <ChevronDown size={14} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52 bg-slate-900 border-slate-700">
                {primaryItems.slice(3).map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className="lg:hidden cursor-pointer text-slate-300 hover:text-white focus:text-white focus:bg-slate-800"
                      data-testid={`nav-more-${item.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <Icon className="mr-2 h-4 w-4" />
                      {item.label}
                    </DropdownMenuItem>
                  );
                })}
                {moreItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className="cursor-pointer text-slate-300 hover:text-white focus:text-white focus:bg-slate-800"
                      data-testid={`nav-more-${item.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <Icon className="mr-2 h-4 w-4" />
                      {item.label}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* User Menu */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              className="w-9 h-9 p-0 rounded-full text-slate-400 hover:text-white hover:bg-slate-800"
              data-testid="theme-toggle-btn"
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </Button>

            {/* Credits Pill */}
            {user && !user.is_admin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/credits')}
                className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 font-bold gap-1.5"
                data-testid="credits-pill"
              >
                <Coins size={14} className="text-amber-400" />
                <span className="text-sm">{user?.credits ?? 0}</span>
              </Button>
            )}

            {/* Notification Bell */}
            <NotificationBell />

            {/* Admin Panel Button - visible for admins */}
            {user?.is_admin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin')}
                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20 hover:text-amber-300 font-semibold gap-1.5"
                data-testid="admin-panel-btn"
              >
                <Shield size={14} />
                <span className="hidden sm:inline">Admin</span>
              </Button>
            )}
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="flex items-center gap-2 text-slate-300 hover:text-white"
                  data-testid="user-menu-trigger"
                >
                  <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                    <User size={16} />
                  </div>
                  <span className="hidden sm:block text-sm">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-slate-900 border-slate-700">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-white">{user?.name}</p>
                  <p className="text-xs text-slate-400">{user?.email}</p>
                </div>
                <DropdownMenuSeparator className="bg-slate-700" />
                {user?.is_admin && (
                  <>
                    <DropdownMenuItem 
                      onClick={() => navigate('/admin')}
                      className="cursor-pointer text-slate-300 hover:text-white focus:text-white focus:bg-slate-800"
                      data-testid="admin-menu-item"
                    >
                      <Shield className="mr-2 h-4 w-4" />
                      Panel Admin
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-slate-700" />
                  </>
                )}
                <DropdownMenuItem 
                  onClick={() => navigate('/credits')}
                  className="cursor-pointer text-slate-300 hover:text-white focus:text-white focus:bg-slate-800"
                  data-testid="credits-menu-item"
                >
                  <Coins className="mr-2 h-4 w-4" />
                  Mis Créditos
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => navigate('/subscription/manage')}
                  className="cursor-pointer text-slate-300 hover:text-white focus:text-white focus:bg-slate-800"
                  data-testid="subscription-menu-item"
                >
                  <CreditCard className="mr-2 h-4 w-4" />
                  Mi Suscripción
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-700" />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="cursor-pointer text-red-400 hover:text-red-300 focus:text-red-300 focus:bg-slate-800"
                  data-testid="logout-menu-item"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Cerrar Sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-slate-400 hover:text-white"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-slate-900/95 backdrop-blur-xl border-b border-slate-800 max-h-[70vh] overflow-y-auto">
          <div className="px-4 py-4 grid grid-cols-2 gap-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive 
                      ? 'bg-green-600/20 text-green-400' 
                      : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  <Icon size={20} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
